import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { homeViewFor, inviteAccept, inviteLookup, pathForView, toRole, type ApiError, type InviteLookup } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { Wordmark } from '../shell/SideNav';

// The invite link's page (the Flutter console's invite_accept_page.dart): an invited
// person checks who the invite is for, chooses their own password, and is signed in.
// Signed out, outside the shell, in the sign-in card.

const ROLE_WORD: Record<string, string> = {
  ADMIN: 'Facility Admin',
  CAREGIVER: 'Care Staff',
  STAFF: 'Care Staff',
  FAMILY_MEMBER: 'Family',
  RESIDENT: 'Resident',
  MEMBER: 'Aithora Home member',
  OPERATOR: 'Operator',
  SALES: 'Sales',
  DEVELOPER: 'Developer',
};

const messageOf = (e: unknown) => ((e as ApiError)?.message ? (e as ApiError).message : 'Something went wrong. Try again.');

export function InviteAccept() {
  const { token = '' } = useParams();
  const { client, signIn } = useSession();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InviteLookup | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    inviteLookup(client, { params: { token } })
      .then((r) => {
        if (!live) return;
        setInvite(r);
        if (r.username) setUsername(r.username);
      })
      .catch((e) => live && setLoadErr(messageOf(e)));
    return () => {
      live = false;
    };
  }, [client, token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 10) return setError('Use at least 10 characters.');
    if (password !== confirm) return setError('The two passwords do not match.');
    setBusy(true);
    setError(null);
    try {
      const r = await inviteAccept(client, { params: { token }, body: { password, ...(username.trim() ? { username: username.trim() } : {}) } });
      // Their role must use two-factor: sign-in's enrolment step takes it from here.
      if (r.twofaRequired && r.pendingToken) {
        navigate('/signin', { replace: true, state: { enroll: { pendingToken: r.pendingToken, expiresAt: r.expiresAt } } });
        return;
      }
      if (!r.token) throw new Error('The server did not return a session.');
      await signIn({ ok: true, token: r.token, userId: r.userId, role: r.role, name: r.name ?? null, organizationId: r.organizationId ?? null, expiresAt: r.expiresAt ?? '' });
      const home = homeViewFor(toRole(r.role));
      navigate(home ? pathForView(home) : '/no-access', { replace: true });
    } catch (err) {
      setError(messageOf(err));
      setBusy(false);
    }
  };

  const bad = invite && !invite.valid;
  return (
    <div className="signin">
      <div className="signin__card" data-testid="invite-accept">
        <Wordmark />
        {!invite && !loadErr && (
          <p className="signin__hint" aria-busy="true">
            Checking your invite{'…'}
          </p>
        )}
        {(loadErr || bad) && (
          <>
            <h1 className="signin__title">This invite cannot be used</h1>
            <p className="signin__lead">{bad ? (invite?.error ?? 'The link is not valid.') : loadErr}</p>
            <p className="signin__hint">Ask whoever invited you for a new link. If you already have an account, sign in.</p>
            <div className="signin__links">
              <Link className="linkbtn" to="/signin">
                Go to sign in
              </Link>
            </div>
          </>
        )}
        {invite?.valid && (
          <form onSubmit={submit} noValidate>
            <h1 className="signin__title">Set up your account</h1>
            <p className="signin__lead">
              {`${invite.name ? invite.name + ', you' : 'You'} were invited to ${invite.orgName ?? 'Aithora'} as ${ROLE_WORD[String(invite.role ?? '').toUpperCase()] ?? String(invite.role ?? '').toLowerCase()}. Choose your own password to finish.`}
            </p>
            <label className="field">
              <span className="field__label">Email</span>
              <input className="field__input" value={invite.email ?? ''} readOnly autoComplete="email" />
            </label>
            <label className="field">
              <span className="field__label">Username (optional)</span>
              <input className="field__input" name="username" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">New password</span>
              <input className="field__input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
            <label className="field">
              <span className="field__label">Type it again</span>
              <input className="field__input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </label>
            {error && (
              <p className="signin__error" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? 'Setting up…' : 'Create my account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
